import { environment } from '../../../../environments/environment';
import { hostedGatewayOriginsForHostname } from '../../../../environments/gateway-origins';
import {
  matchesApiOriginPrefix,
  resolveGatewayRequestUrl,
} from '../../../core/interceptors/api-origin.interceptor';

/** True when the URL points at a still image rather than a video stream or clip. */
export function isImageMediaUrl(url: string | null | undefined): boolean {
  if (!url?.trim()) {
    return false;
  }
  return /\.(jpe?g|png|webp|gif)(\?|#|$)/i.test(url.trim());
}

/**
 * Resolves alarm clip / playback media paths for `<img>` and `<video>` elements.
 * Mirrors `apiOriginInterceptor` for `/media` and `/api/*` when `apiOrigin` is set;
 * otherwise prefixes the current page origin for same-origin dev proxying.
 */
export function resolveMediaAssetUrl(url: string | null | undefined): string {
  const candidate = url?.trim();
  if (!candidate) {
    return '';
  }

  if (/^[a-z][a-z\d+.-]*:\/\//i.test(candidate)) {
    return candidate;
  }

  const path = candidate.startsWith('/') ? candidate : `/${candidate}`;
  const hosted = hostedGatewayOriginsForHostname(globalThis.location?.hostname ?? '');
  const configuredOrigin = (hosted?.apiOrigin ?? environment.apiOrigin)?.replace(/\/$/, '') ?? '';
  if (configuredOrigin && matchesApiOriginPrefix(path)) {
    return resolveGatewayRequestUrl(path, configuredOrigin);
  }

  const pageOrigin =
    typeof globalThis !== 'undefined' && globalThis.location?.origin
      ? globalThis.location.origin
      : '';
  return pageOrigin ? `${pageOrigin}${path}` : path;
}

/**
 * Same-origin URL to fetch alarm-clip / snapshot bytes without tainting the canvas.
 * On localhost, Azure Blob SAS URLs go through `/__blob/<host>/…` (dev-server proxy).
 */
export function resolveSameOriginMediaFetchUrl(url: string | null | undefined): string {
  const resolved = resolveMediaAssetUrl(url);
  if (!resolved) {
    return '';
  }

  const hostname = globalThis.location?.hostname ?? '';
  const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
  if (!isLocalDev) {
    return resolved;
  }

  try {
    const pageOrigin = globalThis.location?.origin ?? '';
    const parsed = new URL(resolved, pageOrigin || 'http://localhost');
    if (!parsed.hostname.endsWith('.blob.core.windows.net')) {
      return resolved;
    }
    return `/__blob/${parsed.host}${parsed.pathname}${parsed.search}`;
  } catch {
    return resolved;
  }
}

/** True when the resolved URL must be fetched with `HttpClient` (auth headers). */
export function requiresAuthenticatedMediaFetch(url: string | null | undefined): boolean {
  const resolved = resolveMediaAssetUrl(url);
  if (!resolved) {
    return false;
  }

  if (resolved.includes('/api/')) {
    return true;
  }

  try {
    const pageOrigin =
      typeof globalThis !== 'undefined' && globalThis.location?.origin
        ? globalThis.location.origin
        : '';
    if (!pageOrigin) {
      return false;
    }
    return new URL(resolved, pageOrigin).origin === pageOrigin && resolved.includes('/api/');
  } catch {
    return false;
  }
}
